import { Link, useNavigate } from "react-router-dom";
import { Draggable } from "@hello-pangea/dnd";
import { AlertCircle, Clock, GripVertical, MapPin, ListChecks } from "lucide-react";
import { CustomerMark } from "../customers/CustomerBits";
import { PilotTag } from "../PilotBits";
import { getInitials, avatarColorClass } from "../../lib/avatar";
import { formatRelativeDate } from "../../lib/pipeline";
import { dueLabel, inactiveLabel, quickActions, locationLabel, NEXT_ACTION_STAGES } from "../../lib/pipelineCard";
import { Card } from "../ui/card";
import QuickActionButton from "./QuickActionButton";
import ProspectActionsMenu from "./ProspectActionsMenu";

// A touch screen needs 44 px targets (rules DESIGN_SYSTEM §23); a mouse keeps the compact ones.
const TARGET = "h-7 w-7 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11";
const TONES = { danger: "text-red-700", warning: "text-orange-700", muted: "text-slate-600" };
const TONE_ICONS = { danger: AlertCircle, warning: Clock };

// No interactive control sits inside another one: the name is a real link, the grip is the only drag handle, the quick actions are
// real links or buttons and the "…" menu is the full keyboard / touch alternative to dragging. Tab order: name → grip → actions → menu.
export default function PipelineCard({
  prospect: p, index, today, canMove, can, tasksVisible, assigneeName, customerBadge, reverted, selected, dragOff, onMove, onQuick, onOpenPanel, canAssign,
}) {
  const navigate = useNavigate();
  const actions = quickActions(p, { can, hasCustomer: !!customerBadge });
  const inactive = inactiveLabel(p);
  const showNext = tasksVisible && NEXT_ACTION_STAGES.includes(p.status);
  const next = p.next_action;
  const due = next ? dueLabel(next, today) : null;
  const ob = p.onboarding;

  const menu = (
    <ProspectActionsMenu
      prospect={p} actions={actions} can={can} canAssign={canAssign} canMove={canMove} targetClass={TARGET}
      onOpen={() => navigate(`/prospects/${p.id}`)} onQuick={onQuick} onMove={onMove}
    />
  );

  return (
    <Draggable draggableId={p.id} index={index} isDragDisabled={!canMove || !!dragOff}>
      {(drag, snap) => (
        <Card
          ref={drag.innerRef}
          {...drag.draggableProps}
          data-testid={`kanban-card-${p.id}`}
          tabIndex={0}
          aria-label={`${p.pharmacy_name || "Prospect"} : ouvrir la fiche rapide`}
          aria-current={selected ? "true" : undefined}
          onClick={(e) => {
            // A click on the card (not on one of its own controls) opens the quick panel; the name stays a real link to the full record.
            if (snap.isDragging || e.target.closest("a,button,[role=menu],[role=menuitem]")) return;
            onOpenPanel(p);
          }}
          onKeyDown={(e) => {
            if (e.target === e.currentTarget && e.key === "Enter") { e.preventDefault(); onOpenPanel(p); }
          }}
          className={`kanban-card p-3 cursor-pointer border-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora ${selected ? "ring-2 ring-jamora border-jamora" : ""} ${snap.isDragging ? "shadow-xl ring-2 ring-jamora/40 rotate-1" : ""} ${reverted ? "card-reverted" : ""}`}
        >
          <div className="flex items-start gap-1">
            <Link
              to={`/prospects/${p.id}`}
              data-testid={`kanban-card-link-${p.id}`}
              className="min-w-0 flex-1 font-medium text-sm text-slate-900 line-clamp-2 hover:text-jamora hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora rounded-sm"
            >
              {p.pharmacy_name || "Pharmacie sans nom"}
            </Link>
            {canMove && !dragOff ? (
              <button
                type="button"
                {...drag.dragHandleProps}
                aria-label={`Déplacer la carte ${p.pharmacy_name || ""}`.trim()}
                title="Glisser pour déplacer · Espace puis flèches au clavier"
                data-testid={`kanban-handle-${p.id}`}
                className={`${TARGET} shrink-0 flex items-center justify-center rounded text-slate-500 hover:bg-slate-100 cursor-grab active:cursor-grabbing focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora`}
              >
                <GripVertical size={16} />
              </button>
            ) : null}
            {actions.length === 0 ? menu : null}
          </div>

          {(customerBadge || p.pilot_status || inactive) ? (
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <CustomerMark badge={customerBadge} />
              {p.pilot_status ? <PilotTag status={p.pilot_status} testid={`kanban-pilot-${p.id}`} /> : null}
              {inactive ? <span data-testid={`kanban-inactive-${p.id}`} className="inline-flex items-center rounded bg-amber-50 px-1.5 py-0.5 text-2xs font-medium text-amber-800">{inactive}</span> : null}
            </div>
          ) : null}

          {p.status === "lost" ? (
            <div className="mt-1.5 text-xs text-slate-700" data-testid={`kanban-loss-${p.id}`}>
              <span className="font-medium">Motif :</span> {p.loss_reason || "non renseigné"}
            </div>
          ) : null}

          {p.status === "onboarding_in_progress" && ob ? (
            <div className="mt-1.5" data-testid={`kanban-onboarding-${p.id}`}>
              <div className="text-xs text-slate-700 truncate">Prochaine étape : <span className="font-medium">{ob.next_step || "aucune"}</span></div>
              <div className="mt-1 flex items-center gap-2">
                <div className="h-1.5 flex-1 rounded-full bg-slate-100" role="progressbar" aria-valuemin={0} aria-valuemax={ob.total} aria-valuenow={ob.done} aria-label="Avancement de l'onboarding">
                  <div className="h-full rounded-full bg-jamora" style={{ width: `${ob.total ? Math.round((ob.done / ob.total) * 100) : 0}%` }} />
                </div>
                <span className="text-2xs font-mono text-slate-600">{ob.done}/{ob.total}</span>
              </div>
            </div>
          ) : null}

          {showNext ? (
            next ? (
              <div className="mt-1.5 flex items-start gap-1.5 text-xs" data-testid={`kanban-next-${p.id}`}>
                <ListChecks size={12} className="mt-0.5 shrink-0 text-slate-500" aria-hidden />
                <div className="min-w-0">
                  <div className="truncate text-slate-800">{next.title}</div>
                  <div className={`${TONES[due.tone]} inline-flex items-center gap-1 font-semibold`} data-testid={`kanban-due-${p.id}`}>
                    {TONE_ICONS[due.tone] ? (() => { const ToneIcon = TONE_ICONS[due.tone]; return <ToneIcon size={11} aria-hidden />; })() : null}
                    {due.text}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-1.5 text-xs italic text-slate-500" data-testid={`kanban-next-${p.id}`}>Aucune action visible</div>
            )
          ) : null}

          {locationLabel(p) ? (
            <div className="flex items-center gap-1 text-xs text-slate-600 mt-1.5">
              <MapPin size={11} aria-hidden />
              <span className="truncate" data-testid={`kanban-location-${p.id}`}>{locationLabel(p)}</span>
            </div>
          ) : null}

          {actions.length > 0 ? (
            <div className="mt-2 flex items-center gap-1.5">
              {actions.map((a) => <QuickActionButton key={a.id} action={a} prospect={p} onQuick={onQuick} />)}
              <div className="ml-auto">{menu}</div>
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-slate-100">
            {p.assigned_to ? (
              <div className="flex items-center gap-1.5 min-w-0">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-2xs font-semibold flex-shrink-0 ${avatarColorClass(p.assigned_to)}`} aria-hidden>
                  {getInitials(assigneeName)}
                </div>
                <span className="text-xs text-slate-700 truncate">{assigneeName || "Utilisateur"}</span>
              </div>
            ) : (
              <span className="text-xs text-slate-600">Non assigné</span>
            )}
            <span className="text-2xs text-slate-600 flex-shrink-0" title="Dernière activité enregistrée sur ce prospect" data-testid={`kanban-activity-${p.id}`}>
              Activité · {formatRelativeDate(p.last_activity_at)}
            </span>
          </div>
        </Card>
      )}
    </Draggable>
  );
}
